/**
 * Recovery for the error a deploy causes in browsers that were already open.
 *
 * Next.js splits the app into content-hashed chunks. When a new build ships, the
 * hashes change and the old files stop being served — so a tab holding the
 * previous HTML asks for a chunk that no longer exists, and the router throws
 * `ChunkLoadError` on the next navigation. The page is not broken; it is stale.
 *
 * This is live, not theoretical: on 2026-08-20 alone the client-error endpoint
 * took ten of these between 03:13 and 05:03Z, across /homes, /compare and a
 * listing detail page, in both the route and global boundaries — every one of
 * them a person who clicked something and got an error page instead.
 *
 * **BUT NOT EVERY ChunkLoadError IS A STALE DEPLOY, and reloading on the others
 * is worse than doing nothing.** A steady ~10/hour arrive for one file,
 * `2uxcro-59m8f1.css` — the Mapbox stylesheet, lazily loaded on /homes, the only
 * page with a map — from real Chrome and Firefox on Windows and Mac. That chunk
 * is not missing: it answers 200 with 40KB of valid CSS, while a genuinely
 * unknown path 404s. They are lazy loads ABORTED because the visitor navigated
 * away, and reloading the page under somebody who has already left, or is about
 * to, is a page-reload for a request they cancelled themselves.
 *
 * So the file is checked before reloading. A chunk that still serves means the
 * failure was transient and the boundary's own reset will do; a chunk that is
 * gone is the stale deploy this was written for.
 *
 * `reset()` cannot fix it. The boundary's reset re-renders the same tree, which
 * asks for the same missing file; only a full document load fetches HTML naming
 * chunks that exist. So the recovery is a reload.
 */

/** sessionStorage key holding the timestamp of the last recovery reload. */
export const CHUNK_RELOAD_KEY = 'goreal:chunk-reload-at';

/**
 * How long a recent reload suppresses another one.
 *
 * A reload loop is a far worse failure than the error page it replaces: the user
 * cannot read the error, cannot navigate away, and cannot tell us what happened.
 * If a fresh document STILL throws ChunkLoadError the deploy is genuinely broken
 * — a missing asset rather than a stale one — and the honest response is to stop
 * and show the error. Thirty seconds is longer than any legitimate load, so a
 * second failure inside the window is evidence of that rather than of staleness.
 */
export const CHUNK_RELOAD_WINDOW_MS = 30_000;

/**
 * Is this the stale-deploy error?
 *
 * Matched on both the name and the message because the two Next.js runtimes
 * disagree: Turbopack throws an Error whose `name` is "ChunkLoadError" and whose
 * message reads "Failed to load chunk …", while webpack's classic form reads
 * "Loading chunk 42 failed". Matching only one of them silently covers half the
 * builds this app ships.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const err = error as { name?: unknown; message?: unknown };
  if (typeof err.name === 'string' && err.name === 'ChunkLoadError') return true;
  const message = typeof err.message === 'string' ? err.message : String(error);
  return (
    /ChunkLoadError/i.test(message) ||
    /Loading (CSS )?chunk .* failed/i.test(message) ||
    /Failed to load chunk/i.test(message)
  );
}

/**
 * Reload once to pick up the current build. Reports whether it did.
 *
 * `false` means the caller should render its error UI: either this was a
 * different error entirely, or a reload was already attempted and did not help.
 *
 * Every storage access is guarded. Safari in private mode throws on
 * sessionStorage, and a recovery path that can itself throw would turn a stale
 * chunk into a blank page.
 */
/**
 * The chunk path Turbopack names in its message, if it named one.
 *
 * "Failed to load chunk /_next/static/chunks/2uxcro-59m8f1.css from module 49207"
 * carries the path; webpack's "Loading chunk 42 failed" carries only an id, and
 * null is the honest answer there — an unidentifiable chunk cannot be checked, so
 * it falls back to the reload it always did.
 */
export function chunkPathFrom(error: unknown): string | null {
  const message =
    typeof (error as { message?: unknown })?.message === 'string'
      ? ((error as { message: string }).message)
      : String(error ?? '');
  const match = message.match(/(\/_next\/static\/[^\s"')]+)/);
  return match ? match[1] : null;
}

/**
 * Does the chunk still exist?
 *
 * `true` means the failure was transient — the visitor navigated away mid-load,
 * or the network blipped — and a reload would take a page away from somebody for
 * a request they cancelled themselves.
 *
 * Any error answering the question is treated as "gone", which errs toward
 * reloading: that is the behaviour this had before the check existed, and it
 * recovers a genuinely stale page rather than stranding it.
 */
async function chunkStillServes(path: string): Promise<boolean> {
  try {
    // cache: 'no-store' so a cached 404 (or a cached hit) cannot answer for the
    // network, which is the whole question being asked.
    const res = await fetch(path, { method: 'GET', cache: 'no-store' });
    return res.ok;
  } catch {
    return false;
  }
}

export async function recoverFromChunkError(
  error: unknown,
  now: number = Date.now(),
): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (!isChunkLoadError(error)) return false;

  // The visitor has already gone. Whatever failed, reloading a page nobody is
  // looking at cannot help and may pull them back to a page they left.
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    return false;
  }

  const path = chunkPathFrom(error);
  if (path && (await chunkStillServes(path))) {
    // The file is there. This was an aborted load, not a stale deploy — the
    // ~10/hour on /homes for the Mapbox stylesheet are all of these.
    return false;
  }

  try {
    const last = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
    if (Number.isFinite(last) && last > 0 && now - last < CHUNK_RELOAD_WINDOW_MS) {
      return false; // already tried; a second reload would be a loop
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
  } catch {
    // No storage means no loop guard, and an unguarded reload can spin forever.
    // Refusing to reload degrades to the current behaviour — an error page — which
    // is recoverable by hand. A loop is not.
    return false;
  }

  window.location.reload();
  return true;
}
